import React from 'react';
import { connect } from 'react-redux';
import { Box, Tabs, Tab, withStyles } from '@material-ui/core';
import { createStyles } from '@material-ui/styles';
import FlipMove from 'react-flip-move';
import queryString from 'stringquery';
import _ from 'lodash';

import Logger from '../../util/Logger';
import { handleErrorWithDialog } from '../../util/Errors';
import { NavigationUtils } from '../../util/NavigationUtils';
import { BrowserChecker } from '../../BrowserChecker';

import { showDialog } from '../../actions/dialog';
import { updateEventLayout } from '../../actions/event';
import { updateNoteworthyResponses } from '../../actions/responses';

import Event from '../../models/Event';
import { EventQuestionType } from '../../models/EventQuestionType';
import { EventUseCase } from '../../models/EventUseCase';
import { EventVisibilityType } from '../../models/EventVisibilityType';
import Response from '../../models/Response';

import {
  CREATOR_REPORTS_ALL_RESPONSES,
  CREATOR_REPORTS_COMMENT_BY_SENTIMENT,
  CREATOR_REPORTS_DISTRIBUTION,
  CREATOR_REPORTS_HISTORICAL_TREND,
  CREATOR_REPORTS_NOTEWORTHY,
  CREATOR_REPORTS_SEGMENTATION,
  CREATOR_REPORTS_SENTIMENT_BY_RATING,
  CREATOR_REPORTS_STATISTICS,
  CREATOR_REPORTS_TOP_RESPONSES,
  CREATOR_REPORTS_AI_INSIGHTS,
  PAGE_LINK_CUSTOMIZE_CREATOR_REPORT,
  PAGE_LINK_SEGMENTATION_HELP
} from '../../constants';

import { CreatorReportAnalytics } from '../../analytics/CreatorReportAnalytics';

import QuestionStats from './QuestionStats';
import ResponsesList from './ResponsesList';
import AverageBreakdown from './AverageBreakdown';
import { ResponseSorts } from './ResponseSorts';
import CommentsBySentiment from './CommentsBySentiment';
import NoteworthySection from './NoteworthySection';
import SegmentationDialog from './SegmentationDialog';
import HistoricalTrend from './HistoricalTrend';
import SentimentByRating from './SentimentByRating';
import Segmentation from './Segmentation';
import SegmentationSection from './SegmentationSection';
import AllResponsesSection from './AllResponsesSection';
import TopResponsesSection from './TopResponsesSection';
import AIInsightsFeaturebasedV1 from './AIInsightsFeaturebasedV1';
import { AutoPageBreaker, StartingHeight } from './AutoPageBreaker';
import { EditSwitch, EnableSectionUpsell, EditArrowSection } from './MainContent';
import { TopBarVisibility } from '../layout/TopBarVisibility';
import { getDefaultSections } from 'src/pages/CreatorReports/CreatorReports';
import AIInsightsSection from './AIInsightsSection';

class Report extends React.Component {

    constructor (props) {
      super(props);

  
      this.state = {
        showNoteworthyDialog: false,
        showSegmentationDialog: false,
        activeTab: 'results', // Default tab is results
        isSummaryView: true, // For AI insights summary view
        currentQuestionNumber: undefined // For AI insights question-specific analysis
      };
    }
  
  
  
    handleTabChange = (tab) => {
      this.setState({ activeTab: tab });
      if (tab === 'ai-insights-feature') {
        // Track analytics for AI Insights tab view
        if (typeof CreatorReportAnalytics !== 'undefined' && 
            CreatorReportAnalytics.aiInsightsViewed) {
          CreatorReportAnalytics.aiInsightsViewed();
        }
        
        // Reset AI insights view state
        this.setState({
          isSummaryView: true,
          currentQuestionNumber: undefined
        });
      }
    };
  
    handleQuestionSelect = (questionNumber) => {
      this.setState({
        isSummaryView: false,
        currentQuestionNumber: questionNumber
      });
    };
  
    onReceivedNoteworthyPosts = (switchEnabled, post) => {
      // Instant visual confirmation
      this.updateNoteworthyPostsVisualLayout(switchEnabled, false);
  
      const { onParentStateChanged } = this.props;
      onParentStateChanged({ noteworthyPosts: post });
    };
  
    onReArrangedNoteworthyPosts = (posts) => {
      const { onParentStateChanged } = this.props;
      onParentStateChanged({ noteworthyPosts: posts });
    };
  
    handleOnSectionsSet = (newSections) => {
      const { onParentStateChanged } = this.props;
      onParentStateChanged({ sections: newSections });
    };
  
    onSegmentationActivated = (enabled) => {
      this.updateSegmentationVisualLayout(enabled);
    };
  
    handleNoteworthySwitchChanged = ($event) => {
      const { showDialog, noteworthyPosts } = this.props;
      const hasResponses = (noteworthyPosts && noteworthyPosts.length > 0);
  
      // If we are turning on the noteworthy switch, and we do not have any noteworthy content
      if ($event && !hasResponses) {
        const onClickLearnMore = ($event) => {
          $event.preventDefault();
          NavigationUtils.openTab(PAGE_LINK_CUSTOMIZE_CREATOR_REPORT);
          CreatorReportAnalytics.customizeNoteworthyInfoModalLearnMore();
        };
  
        showDialog({
          title: 'Select Noteworthy Content',
          body: <div>
            POPin lets you handpick content to showcase in your report.
            If you have nothing to showcase, this section will be excluded from your report.
            <span className="link" onClick={ onClickLearnMore }>&nbsp;Learn more</span>
            <br />
            <br />
          </div>,
          ok: 'Next',
          onOk: () => {
            CreatorReportAnalytics.customizeNoteworthyInfoModal('next');
            this.setState({ showNoteworthyDialog: true });
          },
          cancel: 'Cancel',
          onCancel: () => {
            CreatorReportAnalytics.customizeNoteworthyInfoModal('cancel');
          }
        });
      } else {
        this.updateNoteworthyPostsVisualLayout($event, true);
      }
    };
  
    updateNoteworthyPostsVisualLayout = (show, real) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if (show) {
        removeFromHideLayouts(CREATOR_REPORTS_NOTEWORTHY);
      } else {
        addToHideLayouts(CREATOR_REPORTS_NOTEWORTHY);
      }
  
      if (real) {
        CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_NOTEWORTHY, show);
      }
  
      onParentStateChanged({ isNoteworthyResponsesEnabled: show });
    };
  
    handleTopResponsesSwitchChanged = ($event) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if ($event) {
        removeFromHideLayouts(CREATOR_REPORTS_TOP_RESPONSES);
      } else {
        addToHideLayouts(CREATOR_REPORTS_TOP_RESPONSES);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_TOP_RESPONSES, $event);
  
      onParentStateChanged({ isTopResponsesEnabled: $event });
    };
  
    handleStatsSwitchChanged = ($event) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if ($event) {
        removeFromHideLayouts(CREATOR_REPORTS_STATISTICS);
      } else {
        addToHideLayouts(CREATOR_REPORTS_STATISTICS);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_STATISTICS, $event);
  
      onParentStateChanged({ isStatsEnabled: $event });
    };
  
    handleDistributionSwitchChanged = ($event) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if ($event) {
        removeFromHideLayouts(CREATOR_REPORTS_DISTRIBUTION);
      } else {
        addToHideLayouts(CREATOR_REPORTS_DISTRIBUTION);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_DISTRIBUTION, $event);
  
      onParentStateChanged({ isDistributionEnabled: $event });
    };
  
    handleHistoricalTrendSwitchChanged = ($event) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if ($event) {
        removeFromHideLayouts(CREATOR_REPORTS_HISTORICAL_TREND);
      } else {
        addToHideLayouts(CREATOR_REPORTS_HISTORICAL_TREND);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_HISTORICAL_TREND, $event);
  
      onParentStateChanged({ isHistoricalTrendEnabled: $event });
    };
  
    handleCommentsBySentimentSwitchChanged = ($event) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if ($event) {
        removeFromHideLayouts(CREATOR_REPORTS_COMMENT_BY_SENTIMENT);
      } else {
        addToHideLayouts(CREATOR_REPORTS_COMMENT_BY_SENTIMENT);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_COMMENT_BY_SENTIMENT, $event);
  
      onParentStateChanged({ isCommentsBySentimentEnabled: $event });
    };
  
    handleSentimentByRatingSwitchChanged = ($event) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if ($event) {
        removeFromHideLayouts(CREATOR_REPORTS_SENTIMENT_BY_RATING);
      } else {
        addToHideLayouts(CREATOR_REPORTS_SENTIMENT_BY_RATING);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_SENTIMENT_BY_RATING, $event);
  
      onParentStateChanged({ isSentimentByRatingEnabled: $event });
    };
  
    handleSegmentationNotAvailableLearnMore = ($event) => {
      $event.preventDefault();
      NavigationUtils.openTab(PAGE_LINK_SEGMENTATION_HELP);
    };
  
    handleSegmentationSwitchChanged = ($event) => {
      const { event } = this.props;
      const hasSegmentation = (!!event && !!Event.getRosterId(event));
  
      // If we are turning on the segmentation switch
      if ($event && !hasSegmentation) {
        if (EventVisibilityType.ANYONE_GUEST === Event.getVisibilityType(event)) {
          const { showDialog } = this.props;
          showDialog({
            title: 'Segmentation Unavailable',
            body: (
              <div>
                Segmentation is not available for the 'Unrestricted' permission.
                <br />
                <span
                  className="link clickable"
                  onClick={ this.handleSegmentationNotAvailableLearnMore }>Tell me more</span>
              </div>
            ),
            cancel: 'Cancel'
          });
        } else {
          this.setState({ showSegmentationDialog: true });
        }
      } else {
        this.updateSegmentationVisualLayout($event);
      }
    };
  
    updateSegmentationVisualLayout = (show) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if (show) {
        removeFromHideLayouts(CREATOR_REPORTS_SEGMENTATION);
      } else {
        addToHideLayouts(CREATOR_REPORTS_SEGMENTATION);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_SEGMENTATION, show);
  
      onParentStateChanged({ isSegmentationEnabled: show });
    };
  
    handleAllResponsesSwitchChanged = ($event) => {
      const { addToHideLayouts, removeFromHideLayouts, onParentStateChanged } = this.props;
      if ($event) {
        removeFromHideLayouts(CREATOR_REPORTS_ALL_RESPONSES);
      } else {
        addToHideLayouts(CREATOR_REPORTS_ALL_RESPONSES);
      }
      CreatorReportAnalytics.customizeToggleClicked(CREATOR_REPORTS_ALL_RESPONSES, $event);
  
      onParentStateChanged({ isAllResponsesEnabled: $event });
    };
  
    handleAIInsightsSwitchChanged = ($event) => {
      this.updateAIInsightsVisualLayout($event);
    };
  
    updateAIInsightsVisualLayout = (switchEnabled, updateServer = true) => {
      const { updateEventLayout, event } = this.props;
      if (updateServer) {
        updateEventLayout(Event.getId(event), CREATOR_REPORTS_AI_INSIGHTS, switchEnabled);
      }
    };
  
    onHideNoteworthyDialog = () => {
      this.setState({ showNoteworthyDialog: false });
    };
  
    handleNoteworthyEditClicked = ($event) => {
      $event.preventDefault();
      this.setState({ showNoteworthyDialog: true });
      CreatorReportAnalytics.customizeNoteworthyEditClicked();
    };
  
    onHideSegmentationDialog = () => {
      this.setState({ showSegmentationDialog: false });
    };
  
    handleSegmentationEditClicked = ($event) => {
      $event.preventDefault();
      this.setState({ showSegmentationDialog: true });
    };
  
    restoreEventDefaults = ($event) => {
      CreatorReportAnalytics.customizeRestoreDefaults();
      $event.preventDefault();
  
      const { showDialog } = this.props;
      showDialog({
        title: 'Reset to default report',
        body: (
          <div>
            You are about to reset your report to its default state and overwrite any changes.
            &nbsp;Are you sure you want to continue?
          </div>
        ),
        ok: 'Ok',
        onOk: () => {
          const { onParentStateChanged } = this.props;
          CreatorReportAnalytics.customizeRestoreDefaultsModal('ok');
          // Clear noteworthy
          this.onReceivedNoteworthyPosts(false, []);
  
          // Set sections back to default ordering
          const { event } = this.props;
          onParentStateChanged({
            sections: getDefaultSections(event),
            isNoteworthyResponsesEnabled: false,
            isTopResponsesEnabled: true,
            isStatsEnabled: true,
            isDistributionEnabled: true,
            isSentimentByRatingEnabled: true,
            isHistoricalTrendEnabled: true,
            isCommentsBySentimentEnabled: true,
            isSegmentationEnabled: false,
            isAllResponsesEnabled: true,
            isAIInsightsEnabled: false,
        useFeatureBasedAI: false,
  
            // Flag for layout reset
            reset: true
          }, (parent) => {
            parent.forceUpdate();
          });
        },
        cancel: 'Cancel',
        onCancel: () => {
          CreatorReportAnalytics.customizeRestoreDefaultsModal('cancel');
        }
      });
    };
  
    handleTopRankItemClicked = (response) => {
      const { editing } = this.props;
      Logger.info('Clicked top 5', response);
  
      const { responses } = this.props;
      // We better have responses at this point, else how'd we do this?
      const topListLength = 5;
      const topFive = ResponseSorts.getTopRanked(responses, topListLength);
      let index = -1;
  
      for (let i = 0; i < topListLength; ++i) {
        const r = topFive[i];
        if (Response.getId(response) === Response.getId(r)) {
          index = i;
          break;
        }
      }
  
      if (index >= 0) {
        if (editing) {
          // index is 0 indexed
          CreatorReportAnalytics.customizeTopFiveClicked(index + 1);
        } else {
          CreatorReportAnalytics.topFiveClicked();
        }
      }
    };
  
    handleNoteworthyItemClicked = (response) => {
      const { editing } = this.props;
      Logger.info('Clicked Noteworthy', response);
  
      const { noteworthyPosts } = this.props;
      // We better have responses at this point, else how'd we do this?
      let index = -1;
  
      for (let i = 0; i < noteworthyPosts.length; ++i) {
        const r = noteworthyPosts[i];
        if (Response.getId(response) === Response.getId(r)) {
          index = i;
          break;
        }
      }
  
      if (editing) {
        // index is 0 indexed
        CreatorReportAnalytics.customizeNoteworthyCommentClicked(index + 1);
      } else {
        CreatorReportAnalytics.noteworthyCommentClicked();
      }
    };
  
    getScrollingOffsetDistance = () => {
      const { user, event, collection } = this.props;
  
      const isViewingAs = TopBarVisibility.isViewingAs(user);
      const isShowingHeroRibbon = TopBarVisibility.isShowingHeroRibbon(event);
      const isShowingConcurrentSwitcher = TopBarVisibility.isShowingConcurrentSwitcher(event,
        collection);
  
      // Figure out the offset dynamically based on page content
      // This offset is used so that when a user clicks on the noteworthy response and the page scrolls
      // to it, it is not covered by the floating top bars.
      let scrollingOffsetAmount = 0;
      if (isViewingAs) {
        scrollingOffsetAmount = 20;
      }
  
      if (isShowingHeroRibbon && isShowingConcurrentSwitcher) {
        scrollingOffsetAmount += 200;
      } else if (isShowingHeroRibbon) {
        scrollingOffsetAmount += 150;
      } else if (isShowingConcurrentSwitcher) {
        scrollingOffsetAmount += 130;
      } else {
        scrollingOffsetAmount += 100;
      }
  
      return scrollingOffsetAmount;
    };
  
    renderStatsOverviewSection = (index) => {
      const { event, editing, sections, isStatsVisuallyEnabled, isStatsEnabled, stats } = this.props;
  
      // Stats section is special.
      const statsSection = (isStatsEnabled || editing) && (
        <div className="row no-gutters page-break-avoid">
          <div className="col">
            <div className="card-title dont-mess-with-positioning text-left text-truncate d-print-block d-none">
              OVERVIEW
            </div>
            <div className="print-card-header mx-4" />
            <div className="row no-gutters page-break-avoid">
              <div className="report-card-wrapper page-break-avoid col">
                <div className="report-card report-border col">
                  <div className="card-group-title text-truncate d-print-none">OVERVIEW</div>
                  <QuestionStats event={ event } stats={ stats } />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
  
      return !!editing
        ? (
          <div className="report-card-wrapper col page-break-avoid">
            <div className="row no-gutters">
              <div className={ `report-wrapper col${editing ? '-11' : ''}` }>
                <div className={ `report-card ${!editing ? 'report-border' : ''}` }>
                  { editing && (
                    <div className="editing">
                      <div className="card-title editing-title text-truncate d-print-none">STATISTICS</div>
                      <EditSwitch
                        id={ CREATOR_REPORTS_STATISTICS }
                        checked={ isStatsVisuallyEnabled }
                        onChange={ this.handleStatsSwitchChanged }
                      />
                    </div>
                  ) }
                  { (
                    (isStatsEnabled && !editing) ||
                    (isStatsVisuallyEnabled && editing)
                  ) ? statsSection
                    : editing
                      ? (
                        <EnableSectionUpsell content="Show question stats in the report." />
                      )
                      : null }
                </div>
              </div>
              <EditArrowSection
                name="statistics"
                sections={ sections }
                onSectionsSet={ this.handleOnSectionsSet }
                editing={ editing }
                index={ index } />
            </div>
          </div>
        )
        : statsSection;
    };
  
    renderDistributionSection = (index) => {
      const {
        event,
        editing,
        sections,
        isDistributionVisuallyEnabled,
        isDistributionEnabled,
        ideas,
        stats,
        isPdfRender,
        pdfRatingDistributionByIdeaId,
        pdfCommentsByIdeaId
      } = this.props;
  
      const isOpenEnded = EventQuestionType.isOpenEndedType(Event.getQuestionType(event));
      return ((isDistributionEnabled || editing) && !isOpenEnded) && (
        <div className="report-card-wrapper col page-break-avoid">
          <div className="row no-gutters">
            <div className={ `report-wrapper col${editing ? '-11' : ''}` }>
              <div className={ `report-card ${!editing ? 'report-border' : ''}` }>
                { editing && (
                  <div className="editing">
                    <EditSwitch
                      id={ CREATOR_REPORTS_DISTRIBUTION }
                      checked={ isDistributionVisuallyEnabled }
                      onChange={ this.handleDistributionSwitchChanged }
                    />
                  </div>
                ) }
                <div
                  className={ `card-title text-truncate ${editing && 'editing-title'}` }>
                  { !(event.question_type === 'multiple_rating' || event.question_type === 'stack_rank') ? 'BREAKDOWN' : 'SUMMARY' }
                </div>
  
                { (
                  (isDistributionEnabled && !editing) ||
                  (editing && isDistributionVisuallyEnabled)
                ) ? (
                  <AverageBreakdown
                    event={ event }
                    stats={ stats }
                    ideas={ ideas }
                    padding={ true }
                    isPdfRender={ isPdfRender }
                    pdfRatingDistributionByIdeaId={ pdfRatingDistributionByIdeaId }
                    pdfCommentsByIdeaId={ pdfCommentsByIdeaId } />
                )
                  : editing
                    ? <EnableSectionUpsell content="Show Distribution in the report" />
                    : null }
              </div>
            </div>
            <EditArrowSection
              name="distribution"
              sections={ sections }
              onSectionsSet={ this.handleOnSectionsSet }
              editing={ editing }
              index={ index } />
          </div>
        </div>
      );
    };
  

  
    renderHistoricalTrendSection = (index) => {
      const {
        event,
        collection,
        pulseStats,
        editing,
        sections,
        onActiveEventChanged,
        isHistoricalTrendVisuallyEnabled,
        isHistoricalTrendEnabled,
        isPdfRender
      } = this.props;
  
      // Do this here I guess
      // If the event is a BTM we do not show the trend chart
      if (EventUseCase.isBeforeQuestion(Event.getUseCase(event))) {
        return null;
      }
  
      return (isHistoricalTrendEnabled ||
        // When editing, do not show pulse stats if it because of an error.
        ((pulseStats && pulseStats.length > 0) && editing)) && (
        <div className="report-card-wrapper col page-break-avoid">
          <div className="row no-gutters">
            <div className={ `report-wrapper col${editing ? '-11' : ''}` }>
              <div className={ `report-card ${!editing ? 'report-border' : ''}` }>
                { editing && (
                  <div className="editing">
                    <EditSwitch
                      id={ CREATOR_REPORTS_HISTORICAL_TREND }
                      checked={ isHistoricalTrendVisuallyEnabled }
                      onChange={ this.handleHistoricalTrendSwitchChanged }
                    />
                  </div>
                ) }
                <div
                  className={ `card-title text-truncate ${editing && 'editing-title'}` }>
                  HISTORICAL TREND
                </div>
  
                { /* Don't need to check collection here because the section overall checks. */ }
                { (
                  (isHistoricalTrendEnabled && !editing) ||
                  (isHistoricalTrendVisuallyEnabled && editing)
                ) ? (
                    <HistoricalTrend
                      pulseStats={ pulseStats }
                      onChartIndexClicked={ onActiveEventChanged }
                      event={ event }
                      editing={ editing }
                      isPdfRender={ isPdfRender }
                      collection={ collection } />
                  )
                  : editing
                    ? (
                      <EnableSectionUpsell content="Show historical trend in the report." />
                    )
                    : null }
              </div>
            </div>
            <EditArrowSection
              name="historical_trend"
              sections={ sections }
              onSectionsSet={ this.handleOnSectionsSet }
              editing={ editing }
              index={ index } />
          </div>
        </div>
      );
    };
  
    renderCommentsBySentimentSection = (index) => {
      const {
        event,
        editing,
        sections,
        stats,
        isCommentsBySentimentVisuallyEnabled,
        isCommentsBySentimentEnabled
      } = this.props;
  
      const questionType = Event.getQuestionType(event);
      const isOpenEnded = EventQuestionType.isOpenEndedType(questionType);
  
      return (isCommentsBySentimentEnabled || (editing && Event.isSentimentEnabled(event))) && (
        <div className="report-card-wrapper col page-break-avoid">
          <div className="row no-gutters">
            <div className={ `report-wrapper col${editing ? '-11' : ''}` }>
              <div className={ `report-card ${!editing ? 'report-border' : ''}` }>
                { editing && (
                  <div className="editing">
                    <EditSwitch
                      id={ CREATOR_REPORTS_COMMENT_BY_SENTIMENT }
                      checked={ isCommentsBySentimentVisuallyEnabled }
                      onChange={ this.handleCommentsBySentimentSwitchChanged }
                    />
                  </div>
                ) }
                <div
                  className={ `card-title text-truncate ${editing && 'editing-title'}` }>
                  SENTIMENT
                </div>
  
                { (
                  (isCommentsBySentimentEnabled && !editing) ||
                  (editing && isCommentsBySentimentVisuallyEnabled)
                ) ? <CommentsBySentiment event={ event } stats={ stats } />
                  : editing
                    ? (
                      <EnableSectionUpsell
                        content={ `Show percentage of ${isOpenEnded
                          ? 'Responses'
                          : 'Comments'} by sentiment in the report.` } />
                    )
                    : null
                }
              </div>
            </div>
            <EditArrowSection
              name="sentiment"
              sections={ sections }
              onSectionsSet={ this.handleOnSectionsSet }
              editing={ editing }
              index={ index } />
          </div>
        </div>
      );
    };
  
    renderSentimentSection = (index) => {
      const {
        event,
        sentimentByRating,
        editing,
        sections,
        isSentimentByRatingVisuallyEnabled,
        isSentimentByRatingEnabled
      } = this.props;
  
      // Disabled on the client for now.
      const DISABLED_SENTIMENT_BY_RATING = true;
      if (DISABLED_SENTIMENT_BY_RATING) {
        return null;
      } else {
        const questionType = Event.getQuestionType(event);
  
        return (isSentimentByRatingEnabled ||
          (EventQuestionType.isScaleType(questionType) && editing &&
            Event.isSentimentEnabled(event))) && (
          <div className="report-card-wrapper col page-break-avoid">
            <div className="row no-gutters">
              <div className={ `report-wrapper col${editing ? '-11' : ''}` }>
                <div className={ `report-card ${!editing ? 'report-border' : ''}` }>
                  { editing && (
                    <div className="editing">
                      <EditSwitch
                        id={ CREATOR_REPORTS_SENTIMENT_BY_RATING }
                        checked={ isSentimentByRatingVisuallyEnabled }
                        onChange={ this.handleSentimentByRatingSwitchChanged }
                      />
                    </div>
                  ) }
                  <div
                    className={ `card-title text-truncate ${editing && 'editing-title'}` }>
                    SENTIMENT BY RATING
                  </div>
  
                  { /* Don't need to check question type because container checks */ }
                  { (
                    (isSentimentByRatingEnabled && !editing) ||
                    (editing && isSentimentByRatingVisuallyEnabled)
                  ) ? <SentimentByRating event={ event } sentimentByRating={ sentimentByRating } />
                    : editing
                      ? (
                        <EnableSectionUpsell content="Show sentiment by user rating in the report." />
                      )
                      : null
                  }
                </div>
              </div>
              <EditArrowSection
                name="sentiment_by_rating"
                sections={ sections }
                onSectionsSet={ this.handleOnSectionsSet }
                editing={ editing }
                index={ index } />
            </div>
          </div>
        );
      }
    };
  

  
    mapSectionNameToSection = ({ name }, index, isNewReportFormat, reportContext) => {
      const {
        event,
        editing,
        sections,
        isNoteworthyResponsesEnabled,
        isNoteworthyResponsesVisuallyEnabled,
        noteworthyPosts,
        isAllResponsesEnabled,
        responses,
        aiInsightsProps,
        segmentation,
        isSegmentationVisuallyEnabled,
        isSegmentationEnabled,
        isTopResponsesVisuallyEnabled,
        isTopResponsesEnabled,
        isAllResponsesVisuallyEnabled,
        isPdfRender
      } = reportContext.props;
      const { 
        handleNoteworthyEditClicked, 
        handleNoteworthySwitchChanged, 
        onReArrangedNoteworthyPosts, 
        handleOnSectionsSet, 
        onHideNoteworthyDialog, 
        onReceivedNoteworthyPosts, 
        handleQuestionSelect, 
        handleSegmentationSwitchChanged, 
        handleSegmentationEditClicked, 
        getScrollingOffsetDistance 
      } = reportContext.reportComponent;

      switch (name) {
        case CREATOR_REPORTS_NOTEWORTHY:
          return this.renderNoteworthySection(
            event, editing, isNoteworthyResponsesEnabled, isNoteworthyResponsesVisuallyEnabled, noteworthyPosts,
            reportContext.state.showNoteworthyDialog, handleNoteworthyEditClicked, handleNoteworthySwitchChanged,
            onReArrangedNoteworthyPosts, getScrollingOffsetDistance, onHideNoteworthyDialog, onReceivedNoteworthyPosts
          );
        case CREATOR_REPORTS_TOP_RESPONSES:
          return (!isNewReportFormat && this.renderTopResponsesSection(
            event, responses, editing, sections, isTopResponsesVisuallyEnabled, isTopResponsesEnabled
          )) || null;
        case CREATOR_REPORTS_STATISTICS:
          return (!isNewReportFormat && this.renderStatsOverviewSection(index)) || null;
        case CREATOR_REPORTS_DISTRIBUTION:
          return this.renderDistributionSection(index) || null;
        case CREATOR_REPORTS_HISTORICAL_TREND:
          return (!isNewReportFormat && this.renderHistoricalTrendSection(index)) || null;
        case CREATOR_REPORTS_COMMENT_BY_SENTIMENT:
          return (!isNewReportFormat && this.renderCommentsBySentimentSection(index)) || null;
        case CREATOR_REPORTS_SENTIMENT_BY_RATING:
          return (!isNewReportFormat && this.renderSentimentSection(index)) || null;
        case CREATOR_REPORTS_SEGMENTATION:
          return this.renderSegmentationSection(
            event, segmentation, editing, sections, isSegmentationVisuallyEnabled, isSegmentationEnabled, index, isPdfRender
          );
        case CREATOR_REPORTS_ALL_RESPONSES:
          return this.renderAllResponsesSection(
            event, responses, editing, sections, isAllResponsesVisuallyEnabled, isAllResponsesEnabled, index
          ) || null;
        case CREATOR_REPORTS_AI_INSIGHTS:
          return (
            <AIInsightsSection
              {...aiInsightsProps}
              handleQuestionSelect={this.handleQuestionSelect}
            />
          ) || null;
        default:
          Logger.error('Section name unknown: ', name, index);
          return null;
      }
    };

    renderTopResponsesSection = (event, responses, editing, sections, isTopResponsesVisuallyEnabled, isTopResponsesEnabled) => {
      return (
        <TopResponsesSection
          event={event}
          responses={responses}
          isPdfRender={this.props.isPdfRender}
          editing={editing}
          sections={sections}
          isTopResponsesVisuallyEnabled={isTopResponsesVisuallyEnabled}
          isTopResponsesEnabled={isTopResponsesEnabled}
          handleTopResponsesSwitchChanged={this.handleTopResponsesSwitchChanged}
          handleOnSectionsSet={this.handleOnSectionsSet}
        />
      );
    };

    renderAllResponsesSection = (event, responses, editing, sections, isAllResponsesVisuallyEnabled, isAllResponsesEnabled, index) => {
      return (
        <AllResponsesSection
          event={event}
          responses={responses}
          isPdfRender={this.props.isPdfRender}
          editing={editing}
          sections={sections}
          isAllResponsesVisuallyEnabled={isAllResponsesVisuallyEnabled}
          isAllResponsesEnabled={isAllResponsesEnabled}
          handleAllResponsesSwitchChanged={this.handleAllResponsesSwitchChanged}
          handleOnSectionsSet={this.handleOnSectionsSet}
          index={index}
        />
      );
    };

    renderSegmentationSection = (event, segmentation, editing, sections, isSegmentationVisuallyEnabled, isSegmentationEnabled, index, isPdfRender) => {
      return (
        <SegmentationSection
          event={event}
          segmentation={segmentation}
          editing={editing}
          sections={sections}
          isSegmentationVisuallyEnabled={isSegmentationVisuallyEnabled}
          isSegmentationEnabled={isSegmentationEnabled}
          isPdfRender={isPdfRender}
          handleSegmentationSwitchChanged={this.handleSegmentationSwitchChanged}
          handleSegmentationEditClicked={this.handleSegmentationEditClicked}
          handleOnSectionsSet={this.handleOnSectionsSet}
          index={index}
        />
      );
    };

    renderNoteworthySection = (event, editing, isNoteworthyResponsesEnabled, isNoteworthyResponsesVisuallyEnabled, noteworthyPosts, showNoteworthyDialog, handleNoteworthyEditClicked, handleNoteworthySwitchChanged, onReArrangedNoteworthyPosts, getScrollingOffsetDistance, onHideNoteworthyDialog, onReceivedNoteworthyPosts) => {
      return (
        <NoteworthySection
          event={event}
          editing={editing}
          isPdfRender={this.props.isPdfRender}
          isNoteworthyResponsesEnabled={isNoteworthyResponsesEnabled}
          isNoteworthyResponsesVisuallyEnabled={isNoteworthyResponsesVisuallyEnabled}
          noteworthyPosts={noteworthyPosts}
          showNoteworthyDialog={showNoteworthyDialog}
          handleNoteworthyEditClicked={handleNoteworthyEditClicked}
          handleNoteworthySwitchChanged={handleNoteworthySwitchChanged}
          onReArrangedNoteworthyPosts={onReArrangedNoteworthyPosts}
          getScrollingOffsetDistance={this.getScrollingOffsetDistance}
          onHideNoteworthyDialog={onHideNoteworthyDialog}
          onReceivedNoteworthyPosts={onReceivedNoteworthyPosts}
        />
      );
    };
  
    handleCustomizeHelpClicked = ($event) => {
      $event.preventDefault();
      CreatorReportAnalytics.customizeInfoIconClicked();
      NavigationUtils.openTab(PAGE_LINK_CUSTOMIZE_CREATOR_REPORT);
    };
  
    // The ordered list of rendered sections is only the visually displayed
    // list of cards in the order that they are displayed.
    orderedListOfRenderedSections = [];
    walkTheList = (pagebreaker, startingHeight, list, index) => {
      // We are walking still, not ready yet. Don't print me.
      if (index === 0) {
        const { onParentStateChanged } = this.props;
        onParentStateChanged({ listWalked: false });
      }
  
      Logger.info('Walk the list');
      if (!_.isEqual(list, this.orderedListOfRenderedSections)) {
        Logger.warn('List has changed mid walk, stop');
        return;
      }
  
      const name = list[index];
      const { noteworthyPosts, responses } = this.props;
      const hasResponses = (responses && responses.length > 0);
      const hasNoteworthy = (noteworthyPosts && noteworthyPosts.length > 0);
      const allResponses = hasResponses ? ResponseSorts.sortByMostPopularTopLevel(responses) : [];
  
      const callback = () => {
        if (!_.isEqual(list, this.orderedListOfRenderedSections)) {
          Logger.warn('List has changed mid walk callback, stop');
          return;
        }
  
        const { onParentStateChanged } = this.props;
        let i = index + 1;
        if (i < list.length) {
          this.walkTheList(pagebreaker, startingHeight, list, i);
        } else {
          Logger.info('Done walking the list');
          setTimeout(() => onParentStateChanged({ listWalked: true }), this.props.isPdfRender ? 1200 : 500);
        }
      };
  
      if (name === CREATOR_REPORTS_ALL_RESPONSES) {
        if (hasResponses) {
          Logger.info('Check if all_responses needs page break');
          pagebreaker.applyPageBreakIfNeeded(name,
            `CARD-${CREATOR_REPORTS_ALL_RESPONSES}`,
            `${Response.getId(allResponses[0])}`,
            startingHeight, callback);
        } else {
          // If the list is empty, just page-break-avoid
          Logger.info('Apply avoid on empty all_responses');
          pagebreaker.trackHeight(name, `CARD-${name}`, startingHeight,
            callback);
        }
      } else if (name === CREATOR_REPORTS_NOTEWORTHY) {
        if (hasNoteworthy) {
          Logger.info('Check if noteworthy needs page break');
          pagebreaker.applyPageBreakIfNeeded(name,
            `CARD-${CREATOR_REPORTS_NOTEWORTHY}`,
            `TOP-${Response.getId(noteworthyPosts[0])}`,
            startingHeight, callback);
        } else {
          // If the list is empty, just page-break-avoid
          Logger.info('Apply avoid on empty noteworthy');
          pagebreaker.trackHeight(name, `CARD-${name}`, startingHeight,
            callback);
        }
      } else if (name === CREATOR_REPORTS_TOP_RESPONSES) {
        if (hasResponses) {
          Logger.info('Check if top responses needs page break');
          pagebreaker.applyPageBreakIfNeeded(name,
            `CARD-${CREATOR_REPORTS_TOP_RESPONSES}`,
            `TOP-${Response.getId(allResponses[0])}`,
            startingHeight, callback);
        } else {
          // If the list is empty, just page-break-avoid
          Logger.info('Apply avoid on empty top_responses');
          pagebreaker.trackHeight(name, `CARD-${name}`, startingHeight,
            callback);
        }
      } else {
        Logger.info('Track height of card, keep walking');
        pagebreaker.trackHeight(name, `CARD-${name}`, startingHeight,
          callback);
      }
    };
  
    // We only want one list walk to happen at a time.
    // This timeoutId variable holds the current list walk attempt.
    // Always clear any old ones before attempting a new list walk.
    timeoutId = null;
  
    componentDidUpdate () {
      if (!!this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
  
      this.timeoutId = setTimeout(() => {
        const list = this.orderedListOfRenderedSections;
        if (!!list && list.length > 0) {
          Logger.info(this.orderedListOfRenderedSections);
          const pagebreaker = new AutoPageBreaker();
          const height = new StartingHeight();
          this.walkTheList(pagebreaker, height, list, 0);
        }
      }, this.props.isPdfRender ? 250 : 100);
    }
  
    render () {
      const {
        event,
        collection,
        responses,
        noteworthyPosts,
        roster,
        stats,
        editing,
        sections,
        disablePublish,
        onEditModeClicked,
        onExitEditMode,
        location,
        isTopResponsesVisuallyEnabled,
        isTopResponsesEnabled,
        isAllResponsesVisuallyEnabled,
        isAIInsightsEnabled,
        user,
        isPdfRender
      } = this.props;
  
      const isNewReportFormat = EventQuestionType.isMultipleRatingType(Event.getQuestionType(event)) || EventQuestionType.isStackRankType(Event.getQuestionType(event));
  
      const { showNoteworthyDialog, showSegmentationDialog, isSummaryView, currentQuestionNumber } = this.state;

      const aiInsightsProps = {
        event,
        responses,
        stats,
        isAIInsightsEnabled,
        user,
        isSummaryView,
        currentQuestionNumber
      };
  
      // Keep track of what we are actually rendering, in order.
      this.orderedListOfRenderedSections = [];
  
      const renderEverything = sections.map((s, i) => {
        if (!s || !s.name) {
          return null;
        }
  
        const renderThis = this.mapSectionNameToSection(s, i, isNewReportFormat, {
          props: this.props,
          state: this.state,
          reportComponent: this,
          aiInsightsProps: aiInsightsProps
        });
        if (!renderThis) {
          return null;
        }
  
        // Keep track of what we are actually rendering, in order.
        this.orderedListOfRenderedSections.push(s.name);
  
        // Apply page breaks in the PDF engine if for some reason dynamic finding fails.
        let breakAvoid = false;
        let breakBefore = false;
        if (!!location.search) {
          const searchParams = queryString(location.search);
          if (!!searchParams.avoids) {
            const avoids = decodeURIComponent(searchParams.avoids);
            if (avoids.indexOf(s.name) >= 0) {
              breakAvoid = true;
            }
          }
  
          if (!!searchParams.befores) {
            const befores = decodeURIComponent(searchParams.befores);
            if (befores.indexOf(s.name) >= 0) {
              breakBefore = true;
            }
          }
        }
  
        return (
          <li
            id={ `CARD-${s.name}` }
            key={ s.name }
            className={ `${!!breakAvoid ? 'page-break-avoid' : ''} ${!!breakBefore
              ? 'page-break-before'
              : ''}` }>
            { renderThis }
          </li>
        );
      });
  
      // Check the current page type and user authorization
      const pathName = window.location.pathname;
      const pathParts = pathName.split('/').filter(part => part.length > 0);
      
      // More precise path detection
      const qIndex = pathParts.indexOf('q');
      const hasEventId = qIndex !== -1 && qIndex + 1 < pathParts.length;
      const eventIdFromPath = hasEventId ? pathParts[qIndex + 1] : null;
      
      // Check if we're on the summary tab
      const summaryTab = pathParts.includes('summary');
      
      // Check if we're on a numbered question page (1, 2, 3, etc)
      const questionNumber = pathParts[pathParts.length - 1];
      const isQuestionPage = !isNaN(questionNumber) && questionNumber > 0;
      
      // Determine page type
      const isOnAnswersPage = hasEventId && !summaryTab && !isQuestionPage;
      const isSummaryPage = summaryTab;
      
      const { activeTab } = this.state;
      
      // Debug logging for path detection
      // console.log('Path detection:', {
      //   pathName,
      //   pathParts,
      //   qIndex,
      //   hasEventId,
      //   isQuestionPage,
      //   questionNumber,
      //   isOnAnswersPage,
      //   isSummaryPage,
      //   summaryTab,
      //   fullUrl: window.location.href,
      //   eventIdFromPath
      // });
      
      // AI Insights is now available to all users.
      const hasEnterpriseAccess = true;
      
      // Show tabs on the answers page (but AI Insights tab only for Enterprise users)
      const showTabs = isOnAnswersPage && !isPdfRender;
      
      // Determine if we should show AI Insights
      // Show AI Insights when:
      // 1. On summary tab
      // 2. On a numbered question page
      // 3. On answers page with AI tab selected (either traditional or feature-based)
      const showAIInsights = summaryTab || isQuestionPage || (showTabs && (activeTab === 'ai-insights' || activeTab === 'ai-insights-feature'));
      
      // Log info for debugging AI Insights visibility
      // console.log('MainContent render - AI Insights debug:', {
      //   pathName,
      //   isOnAnswersPage,
      //   isSummaryPage,
      //   activeTab,
      //   showTabs,
      //   showAIInsights,
      //   eventId: event ? event.id : null
      // });
      
      // Try to get event ID from URL if not available in props
      let eventId = event ? event.id : null;
      if (!eventId) {
        // Use the eventId we extracted earlier from path detection
        if (eventIdFromPath) {
          eventId = eventIdFromPath;
          // console.log('Using eventId from path detection:', eventId);
        }
      }
      
      // Log the final eventId for debugging
      // console.log('Final eventId:', eventId, { 
      //   fromProps: event?.id,
      //   fromURL: eventId !== event?.id,
      //   pathname: window.location.pathname,
      //   isOnAnswersPage,
      //   isSummaryPage
      // });
      
  
      
      return (
        <div className="dashboard-report">
          {showTabs && (
            <div className="answers-tabs" style={{
              display: 'flex',
              marginBottom: '20px',
              borderBottom: '1px solid #ddd',
              backgroundColor: '#f8f9fa'
            }}>
              <div 
                className={`tab ${activeTab === 'results' || activeTab === 'summary' ? 'active' : ''}`}
                onClick={() => this.handleTabChange('results')}
                style={{
                  padding: '12px 24px',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'results' ? '600' : 'normal',
                  borderBottom: activeTab === 'results' ? '3px solid #0078d4' : 'none',
                  color: activeTab === 'results' ? '#0078d4' : '#333',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  ':hover': {
                    backgroundColor: '#f0f0f0',
                    color: '#0078d4'
                  }
                }}
              >
                Results
              </div>
                {/* Only show AI Insights tab for users with Enterprise subscription */}
              {hasEnterpriseAccess && (
                <div 
                  className={`tab ${activeTab === 'ai-insights-feature' ? 'active' : ''}`}
                  onClick={() => this.handleTabChange('ai-insights-feature')}
                  style={{
                    padding: '12px 24px',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'ai-insights-feature' ? '600' : 'normal',
                    borderBottom: activeTab === 'ai-insights-feature' ? '3px solid #0078d4' : 'none',
                    color: activeTab === 'ai-insights-feature' ? '#0078d4' : '#333',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    ':hover': {
                      backgroundColor: '#f0f0f0',
                      color: '#0078d4'
                    }
                  }}
                >
                  AI Insights
                </div>
              )}
            </div>
          )}
  
          { !!editing && (
            <div className="row no-gutters mb-3 px-3">
              <div className="row no-gutters my-auto">
                <span className="font-size-lg mr-3 my-auto">Customize Report</span>
                <i
                  className="material-icons mr-3 my-auto clickable"
                  onClick={ this.handleCustomizeHelpClicked }>help_outline</i>
                <span
                  className="font-size-sm my-auto defaults-link clickable"
                  onClick={ this.restoreEventDefaults }>
                  Restore Defaults
                </span>
              </div>
  
              <div className="fix-ie" />
  
              <button className="btn pop-btn-secondary mr-3" onClick={ onExitEditMode }>
                Cancel
              </button>
              <button
                className="btn pop-btn"
                disabled={ disablePublish }
                onClick={ onEditModeClicked }>
                Publish
              </button>
  
              <div className="offset-1" />
            </div>
          ) }
  
          {/* Show different content based on the page type */}
          <ul>
            {/* Show AI Insights based on page type */}
            {/* Only show AI Insights content for users with Enterprise subscriptions */}
            {hasEnterpriseAccess && (isSummaryPage || isQuestionPage || (showTabs && activeTab === 'ai-insights-feature')) && (
              <li key="ai-insights" className="page-break-avoid">
                <AIInsightsFeaturebasedV1 
                  eventId={eventId} 
                  event={event}
                  stats={stats} // Pass the stats that are available in MainContent
                  key={`ai-insights-feature-${eventId}-${isSummaryPage}-${questionNumber}`}
                />
              </li>
            )}
            
            {/* Show regular content if we're on Summary page or if we're on Answers page with results tab */}
            {(isSummaryPage || !showTabs || activeTab === 'results' || isQuestionPage) && renderEverything}
          </ul>
  
          {/* Original else block removed since we've restructured the logic above */}
          {false && (
            /* If we are entering edit mode, the animation gets too busy, don't enable animation outside of editing. */
            (!!editing && !(BrowserChecker.isIe() || BrowserChecker.isEdge()))
              ? (
                <FlipMove typeName="ul">
                  { renderEverything }
                </FlipMove>
              )
              : (
                <ul>
                  { renderEverything }
                </ul>
              )
          )}
  
          { showSegmentationDialog && (
            <SegmentationDialog
              onHidden={ this.onHideSegmentationDialog }
              onActivated={ this.onSegmentationActivated }
              stats={ stats }
              roster={ roster }
              event={ event }
              collection={ collection } />
          ) }
        </div>
      );
    }
  }

export default Report;
